import React, { useState, useEffect } from 'react'
import { Button, Select, Card, Typography, theme, Space, Row, Col } from 'antd'
import {
  SearchOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
// import DischemMeds from '../../Bookings/components/Search Engine Components/DischemMeds'
import SearchResults from './SearchResults'
import DischemStoreSelector from './DischemStoreSelector'

const { Option } = Select
const { Title, Text } = Typography

const VirtualConsultationSearchCriteria = ({
  onSearch,
  searchFilters = {},
  loading = false,
  onBack,
  onFilterChange,
  searchResults = [],
  error = null,
  totalResults = 0,
  onViewAvailability
}) => {
  const { token } = theme.useToken()

  // Step-by-step field management
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState(new Set())

  // Form state (serviceType is already selected, so we don't need it here)
  const [callType, setCallType] = useState(searchFilters.callType || '')
  const [appointmentReason, setAppointmentReason] = useState(searchFilters.appointmentReason || '')
  const [timeOfDay, setTimeOfDay] = useState(searchFilters.timeOfDay || '')
  const [dischemData, setDischemData] = useState(
    searchFilters.dischem || {
      required: false,
      reqType: 'C', // Default to Collection
      store: null
    }
  )

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false)
  const [showResults, setShowResults] = useState(false)

  // Call type options
  const callTypes = [
    { value: 'video', label: 'Video Call', icon: <VideoCameraOutlined /> },
    { value: 'phone', label: 'Phone Call', icon: <VideoCameraOutlined /> },
    { value: 'whatsapp', label: 'WhatsApp Call', icon: <VideoCameraOutlined /> }
  ]

  // Appointment reason options
  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Prescription Renewal',
    'Medical Certificate',
    'Test Results Discussion',
    'Chronic Disease Management',
    'Mental Health Support',
    'Second Opinion',
    'Urgent Medical Advice',
    'Other'
  ]

  // Time of day options
  const timeOfDayOptions = [
    { value: 'any', label: 'Any time', icon: <ClockCircleOutlined /> },
    { value: 'morning', label: 'Morning (6:00 - 12:00)', icon: <ClockCircleOutlined /> },
    { value: 'afternoon', label: 'Afternoon (12:00 - 18:00)', icon: <ClockCircleOutlined /> },
    { value: 'evening', label: 'Evening (18:00 - 22:00)', icon: <ClockCircleOutlined /> }
  ]

  // Handle field changes and progress to next step
  const handleFieldChange = (field, value, stepNumber) => {
    // Update the specific field
    switch (field) {
      case 'callType':
        setCallType(value)
        break
      case 'appointmentReason':
        setAppointmentReason(value)
        break
      case 'timeOfDay':
        setTimeOfDay(value)
        break
    }

    // Mark step as completed and move to next
    const newCompletedSteps = new Set(completedSteps)
    newCompletedSteps.add(stepNumber)
    setCompletedSteps(newCompletedSteps)

    // Move to next step after a brief delay
    setTimeout(() => {
      if (stepNumber < 4) {
        setCurrentStep(stepNumber + 1)
      }
    }, 300)

    // DO NOT update parent filters here - it triggers auto-search
    // onFilterChange will be called only when user clicks search button
    // const updatedFilters = {
    //   ...searchFilters,
    //   [field]: value
    // }
    // onFilterChange(updatedFilters)
  }

  // Handle Dischem data change
  const handleDischemChange = (data) => {
    setDischemData(data)
    // DO NOT update parent filters here - it triggers auto-search
    // const updatedFilters = {
    //   ...searchFilters,
    //   dischem: data
    // }
    // onFilterChange(updatedFilters)

    // Mark step as completed
    const newCompletedSteps = new Set(completedSteps)
    newCompletedSteps.add(4)
    setCompletedSteps(newCompletedSteps)
  }

  // Check if all required fields are completed (serviceType comes from previous step)
  const canSearch = callType && appointmentReason && timeOfDay

  // Auto-complete dischem step if all other fields are done
  useEffect(() => {
    if (canSearch && currentStep < 4) {
      const newCompletedSteps = new Set(completedSteps)
      newCompletedSteps.add(4)
      setCompletedSteps(newCompletedSteps)
    }
  }, [canSearch, currentStep, completedSteps])

  // Handle search with animation
  const handleSearch = () => {
    if (!canSearch) return

    setIsAnimating(true)

    // Start the search immediately
    onSearch({
      // serviceType is already in searchFilters from previous step
      callType,
      appointmentReason,
      timeOfDay,
      dischem: dischemData
    })

    // Show results panel after animation starts
    setTimeout(() => {
      setShowResults(true)
    }, 300)
  }

  // Render step content
  const renderStep = (stepNumber, title, content) => {
    const isVisible = currentStep >= stepNumber || completedSteps.has(stepNumber)
    const isCompleted = completedSteps.has(stepNumber)

    if (!isVisible) return null

    return (
      <div
        style={{
          marginBottom: '24px',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.3s ease'
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ color: token.colorPrimary, fontSize: '16px' }}>
            {title}
          </Text>
          {isCompleted && <span style={{ marginLeft: '8px', color: '#52c41a', fontSize: '14px' }}>✓</span>}
        </div>
        {content}
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative'
      }}
    >
      {/* Back button */}
      {onBack && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          Back
        </Button>
      )}

      {/* Main search criteria card */}
      <Card
        style={{
          width: '100%',
          maxWidth: '600px',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: 'none',
          opacity: isAnimating ? 0 : 1,
          transition: 'opacity 0.4s ease-out',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Title level={2} style={{ color: token.colorPrimary, marginBottom: '8px' }}>
            Virtual Consultation
          </Title>
          <Text style={{ fontSize: '16px', color: '#666' }}>Please provide your consultation preferences</Text>
        </div>

        {/* Step 1: Contact Method */}
        {renderStep(
          1,
          'How would you like to be contacted?',
          <Select
            size='large'
            placeholder='Choose contact method'
            value={callType}
            onChange={(value) => handleFieldChange('callType', value, 1)}
            style={{ width: '100%' }}
          >
            {callTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                <Space>
                  {type.icon}
                  {type.label}
                </Space>
              </Option>
            ))}
          </Select>
        )}

        {/* Step 2: Reason for Visit */}
        {renderStep(
          2,
          'What is the reason for your visit?',
          <Select
            size='large'
            placeholder='Select reason for consultation'
            value={appointmentReason}
            onChange={(value) => handleFieldChange('appointmentReason', value, 2)}
            style={{ width: '100%' }}
          >
            {appointmentReasons.map((reason) => (
              <Option key={reason} value={reason}>
                {reason}
              </Option>
            ))}
          </Select>
        )}

        {/* Step 3: Time of Day */}
        {renderStep(
          3,
          'Preferred time of day?',
          <Select
            size='large'
            placeholder='Select preferred time'
            value={timeOfDay}
            onChange={(value) => handleFieldChange('timeOfDay', value, 3)}
            style={{ width: '100%' }}
          >
            {timeOfDayOptions.map((time) => (
              <Option key={time.value} value={time.value}>
                <Space>
                  {time.icon}
                  {time.label}
                </Space>
              </Option>
            ))}
          </Select>
        )}

        {/* Step 4: Dischem Options */}
        {renderStep(
          4,
          'Prescription delivery options',
          <div style={{ marginTop: '16px' }}>
            <Select
              size='large'
              placeholder='How would you like to handle prescriptions?'
              value={dischemData.required ? (dischemData.reqType === 'C' ? 'collection' : 'delivery') : 'self'}
              onChange={(value) => {
                if (value === 'self') {
                  handleDischemChange({ required: false, reqType: null, store: null })
                } else {
                  handleDischemChange({
                    required: true,
                    reqType: value === 'collection' ? 'C' : 'D',
                    store: dischemData.store
                  })
                }
              }}
              style={{ width: '100%' }}
            >
              <Option value='self'>I'll handle prescriptions myself</Option>
              <Option value='collection'>Dis-Chem Collection</Option>
              <Option value='delivery'>Dis-Chem Delivery</Option>
            </Select>

            {/* Dis-Chem Store Selection - Show when collection or delivery is selected */}
            {dischemData.required && (
              <div style={{ marginTop: '16px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                  Select Dis-Chem Store
                </Text>
                <DischemStoreSelector
                  value={dischemData.store}
                  onChange={(store) => {
                    const updatedDischem = {
                      ...dischemData,
                      store: store
                    }
                    handleDischemChange(updatedDischem)
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Search Button */}
        {canSearch && (
          <div
            style={{
              marginTop: '40px',
              textAlign: 'center',
              opacity: canSearch ? 1 : 0,
              transform: canSearch ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.3s ease'
            }}
          >
            <Button
              type='primary'
              size='large'
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              style={{
                height: '50px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '25px',
                paddingLeft: '30px',
                paddingRight: '30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
              }}
            >
              Find Doctors
            </Button>
          </div>
        )}
      </Card>

      {/* Results section (fades in to take full screen) */}
      {(isAnimating || showResults) && (
        <div
          style={{
            position: 'absolute',
            left: '20px',
            right: '20px',
            top: '20px',
            bottom: '20px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            opacity: showResults ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            transitionDelay: showResults ? '0.3s' : '0s',
            overflow: 'auto'
          }}
        >
          {/* Render actual search results */}
          <div style={{ padding: '20px', height: '100%' }}>
            <SearchResults
              searchType={searchFilters.searchType || 'general_practitioner'}
              results={searchResults}
              loading={loading}
              onViewAvailability={onViewAvailability}
              error={error}
              totalResults={totalResults}
              searchFilters={searchFilters}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default VirtualConsultationSearchCriteria
